import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YaloThemeProvider } from '@engyalo/design-system';
import { datadogRum } from '@datadog/browser-rum';
import groundcover from '@groundcover/browser';
import { LicenseInfo } from '@mui/x-data-grid-pro';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Provider } from 'react-redux';
import { manifestBase } from 'constants/manifest';
import RoutesApp from './routes';
import './App.css';
import './i18n';
import './index.css';
import pkgjson from '../package.json';
import { loadSegment } from './helpers/segment';
import { setBaseUrlAxiosInstance } from './services/AxiosInstance';
import { setBaseUrlAxiosInstanceYalo } from './services/AxiosInstanceYalo';
import useEnvironment from './hooks/useEnvironments';
import store from './redux/store';
import ErrorModal from './components/ErrorModal';

loadSegment();

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_PRO_KEY || 'unknown');

export enum ignoreErrors {
  NETWORK_ERROR = 'Network Error',
  ERROR_401 = 'Request failed with status code 401',
  IGNORED_EVENT_CANCEL = 'IgnoredEventCancel',
}

export const rumBeforeSend = (event: any) => {
  if (event.type === 'error') {
    const isFalsePositiveError = Object.values(ignoreErrors).find(
      (ignoreErrorString) =>
        event.error.message?.includes(ignoreErrorString) || event.error.stack?.includes(ignoreErrorString)
    );
    if (isFalsePositiveError) {
      return false;
    }
  }
  return true;
};

if (process.env.REACT_APP_DD_TOKEN) {
  datadogRum.init({
    applicationId: `${process.env.REACT_APP_DD_ID}`,
    clientToken: `${process.env.REACT_APP_DD_TOKEN}`,
    site: `${process.env.REACT_APP_DD_SITE}`,
    service: `${process.env.REACT_APP_DD_SERVICE}`,
    env: `${process.env.REACT_APP_DD_ENV}`,
    version: `${pkgjson.version}`,
    sampleRate: 100,
    premiumSampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    beforeSend: rumBeforeSend,
  });
  datadogRum.startSessionReplayRecording();
}

/*
 * groundcover RUM, running alongside Datadog rather than replacing it (DOPS-1802).
 * Both SDKs collect independently: Datadog keeps the existing RUM monitors and
 * dashboards working while groundcover accumulates sessions to rebuild them
 * against. Nothing above changes.
 *
 * Inert unless REACT_APP_GROUNDCOVER_RUM_API_KEY is set, so merging this ships no
 * behaviour change until an environment is deliberately configured.
 *
 * Two deliberate differences from the Datadog block:
 *
 * - No session replay. Datadog records it here at premiumSampleRate 100; the
 *   groundcover SDK needs replay enabled explicitly, and starting a second full
 *   recording of an agent-facing tool is a call for whoever owns Salesdesk, not a
 *   side effect of adding a second SDK.
 * - `rumBeforeSend` is not reused. It drops known false-positive errors by
 *   message, which is Datadog-event-shaped and would have to be rewritten
 *   against this SDK's payload. The same noise will appear in groundcover until
 *   it is, which is worth knowing before anyone reads the error counts here as a
 *   like-for-like comparison.
 */
if (process.env.REACT_APP_GROUNDCOVER_RUM_API_KEY) {
  groundcover.init({
    // Ingestion key stays in the environment, like REACT_APP_DD_TOKEN above.
    // dsn and cluster are tenant endpoints rather than credentials, so they sit
    // here for the same reason REACT_APP_DD_SITE could have been inlined.
    apiKey: process.env.REACT_APP_GROUNDCOVER_RUM_API_KEY,
    dsn: 'https://kjv7tl1gb.platform.grcv.io',
    cluster: 'yalo-backend-gcp',
    appId: `${process.env.REACT_APP_DD_SERVICE}`,
    environment: `${process.env.REACT_APP_DD_ENV}`,
    // Source maps resolve against this; without it stack traces stay minified.
    releaseId: `${pkgjson.version}`,
    options: {
      privacy: {
        level: 'mask-sensitive',
      },
    },
  });
}

const noop = () => {};

const App = () => {
  const { getInstance, getUrl } = useEnvironment();
  const { t } = useTranslation();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const generateManifest = (instance: string) => {
    const manifest = {
      ...manifestBase,
      start_url: `/${instance}/`,
      scope: `/${instance}/`,
    };

    const link = document.createElement('link');
    link.rel = 'manifest';
    const stringManifest = JSON.stringify(manifest);
    link.setAttribute('href', `data:application/json;charset=utf-8,${encodeURIComponent(stringManifest)}`);
    document.head.appendChild(link);
  };

  useEffect(() => {
    const instance = getInstance();
    const url = getUrl('http');

    async function checkInstance() {
      try {
        await fetch(url, { method: 'HEAD' });
        if (instance) {
          generateManifest(instance);
        }
      } catch {
        setShowErrorModal(true);
      }
    }

    setBaseUrlAxiosInstance(url);
    setBaseUrlAxiosInstanceYalo(instance);
    checkInstance();
  }, []);

  return (
    <Provider store={store}>
      <YaloThemeProvider>
        {showErrorModal ? (
          <ErrorModal
            open={showErrorModal}
            title={t('invalidInstanceTitle')}
            message={t('invalidInstanceMessage')}
            handleClose={noop}
          />
        ) : (
          <RoutesApp />
        )}
      </YaloThemeProvider>
    </Provider>
  );
};

export default App;
